<template>
  <div class="modal fade show pos-table-modal" aria-labelledby="posOrderTitle" style="display: block; background: #1c1d1c33">
    <div class="modal-dialog modal-dialog-centered pos-table-dialog">
      <div class="modal-content">
        <div class="d-flex justify-content-between align-items-center px-3 py-2 modal-head">
          <h4 class="mb-0">{{ headerTitle }}</h4>
          <div class="d-flex align-items-center">
            <button
                v-if="billingMode"
                type="button"
                class="btn btn-outline-secondary btn-sm mr-3"
                @click="disableBillingMode"
            >
              <i class="fas fa-arrow-left mr-1"></i> Back to Order
            </button>
            <button
                @click="onClickClose"
                type="button"
                class="btn-close"
                aria-label="Close"
            ></button>
          </div>
        </div>

        <div v-if="selectable" class="px-3 py-2 border-bottom table-picker">
          <label class="col-form-label mb-0 mr-2">Table</label>
          <AsyncSelect
              placeholder="Select Table"
              v-model="selectedTable"
              :api-service="fetchRestaurantTablesDropdown"
              :format-label="formatTableLabel"
          />
        </div>

        <div class="modal-body">
          <Loader v-if="loading"/>
          <p v-if="!loading && selectable && !selectedTable" class="text-center text-muted my-5">
            Select a table to start the order
          </p>
          <div v-if="!loading && selectable && isSelectedOccupied" class="text-center occupied-note my-5">
            <i class="fas fa-ban mb-2"></i>
            <p class="mb-0">This table is occupied. Please select an open table.</p>
          </div>
          <div v-if="!loading && parentGroup.length && tableId && !isSelectedOccupied && !billingMode" class="pos-order-wrap">
            <PosOrderBilling
                :key="tableId"
                :parentGroup="parentGroup"
                :companyId="companyId"
                :locations="locations"
                :productId="productId"
                :showFinalBill="true"
                v-model:locationId="locationId"
                @resetIds="onClickClose"
                @goToBilling="goToBilling"
            />
          </div>

          <div v-if="!loading && parentGroup.length && billingMode" class="pos-order-wrap">
            <PosBilling
                :parentGroup="parentGroup"
                :companyId="companyId"
                :locations="locations"
                :productId="productId"
                v-model:locationId="locationId"
                @resetIds="onClickClose"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {computed, onMounted, ref, watch} from 'vue';
import {useRoute, useRouter} from 'vue-router';
import PosOrderBilling from '@/components/molecule/company/pos-invoice/PosOrderBilling.vue';
import PosBilling from '@/components/molecule/company/pos-invoice/PosBilling.vue';
import AsyncSelect from '@/components/molecule/input-field/AsyncSelect.vue';
import Loader from '@/components/atom/LoaderComponent';
import handleInventory from '@/services/modules/inventory';
import handleBusinessesLocations from '@/services/modules/businessesLocations';
import handleDropdowns from '@/services/modules/dropdowns';

const props = defineProps({
  tableName: {
    type: String,
    default: ''
  },
  selectable: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(['close']);

const route = useRoute();
const router = useRouter();
const {fetchGroupParent} = handleInventory();
const {fetchBusinessLocationsList} = handleBusinessesLocations();
const {fetchRestaurantTablesDropdown} = handleDropdowns();

const companyId = route.params.companyId;
const companyQuery = `?company_id=${companyId}`;

const loading = ref(true);
const parentGroup = ref([]);
const locations = ref([]);
const locationId = ref(null);
const productId = ref(null);
const selectedTable = ref(null);
const billingMode = ref(false);

const tableId = computed(() => route.query.service_resource_id);

const showPayNow = computed(() => {
  if (props.selectable) {
    return false;
  }
  if (billingMode.value) {
    return false;
  }
  return !!route.query.order_sales_id;
});

const isSelectedOccupied = computed(() => {
  if (!selectedTable.value) {
    return false;
  }
  return selectedTable.value.is_occupied;
});

const headerTitle = computed(() => {
  if (props.selectable) {
    return 'New Order';
  }
  return props.tableName;
});

const formatTableLabel = (option) => {
  if (option.is_occupied) {
    return `${option.name} (Occupied)`;
  }
  return option.name;
};

onMounted(async () => {
  loading.value = true;
  await getProductHeadGroup();
  await getLocations();
  loading.value = false;
});

watch(selectedTable, async (newTable) => {
  if (!newTable) {
    const clearedQuery = {...route.query};
    delete clearedQuery.service_resource_id;
    delete clearedQuery.order_sales_id;
    await router.replace({query: clearedQuery});
    return;
  }
  if (newTable.is_occupied) {
    const occupiedQuery = {...route.query};
    delete occupiedQuery.service_resource_id;
    delete occupiedQuery.order_sales_id;
    await router.replace({query: occupiedQuery});
    return;
  }
  const newQuery = {...route.query, service_resource_id: newTable.id};
  delete newQuery.order_sales_id;
  await router.replace({query: newQuery});
});

const getProductHeadGroup = async () => {
  const query = `${companyQuery}&product_type=finished_goods,services`;
  const res = await fetchGroupParent(query);
  if (!res.data || !res.data.length) {
    return;
  }
  parentGroup.value = res.data;
};

const getLocations = async () => {
  const locationQuery = `${companyQuery}&location_type=fg_warehouse`;
  const res = await fetchBusinessLocationsList(locationQuery);
  if (!res.status) {
    return;
  }
  locations.value = res.data;
  locationId.value = locations.value[0].id;
};

const enableBillingMode = async () => {
  const newQuery = {...route.query, sales_order_id: route.query.order_sales_id, from_restaurant: 1};
  await router.replace({query: newQuery});
  billingMode.value = true;
};

const goToBilling = async (orderId) => {
  const newQuery = {...route.query, order_sales_id: orderId, sales_order_id: orderId, from_restaurant: 1};
  await router.replace({query: newQuery});
  billingMode.value = true;
};

const disableBillingMode = async () => {
  const newQuery = {...route.query};
  delete newQuery.sales_order_id;
  delete newQuery.from_restaurant;
  await router.replace({query: newQuery});
  billingMode.value = false;
};

const onClickClose = () => {
  emit('close');
};
</script>

<style scoped>
.pos-table-dialog {
  max-width: 1320px;
  width: 95%;
}

.pos-table-modal .modal-content {
  max-height: 92vh;
}

.modal-head {
  background: #DBE7F2;
  border-bottom: 1px solid #b9cee0;
}

.table-picker {
  display: flex;
  align-items: center;
  background: #f7f9fc;
}

.table-picker :deep(.v-select),
.table-picker :deep(.async-select) {
  flex: 1;
  max-width: 320px;
}

.modal-body {
  padding: 8px 12px;
  overflow-y: auto;
  overflow-x: hidden;
}

.occupied-note {
  color: #e17055;
}

.occupied-note i {
  font-size: 32px;
  display: block;
}

.pos-order-wrap {
  border: 1px solid #e3e3e3;
  border-radius: 10px;
  overflow: hidden;
}

.pos-order-wrap :deep(.row) {
  margin-left: 0;
  margin-right: 0;
}

.pos-order-wrap :deep(.content-height) {
  height: 62vh;
  display: flex;
  flex-direction: column;
}

.pos-order-wrap :deep(.pos-right) {
  flex: 1;
  min-height: 0;
  height: auto;
}

.pos-order-wrap :deep(.pay-now-btn) {
  flex: 0 0 auto;
}
</style>
